import { json } from '@remix-run/node'
import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import type { LocaleLoader } from './$locale'
import { PressReleaseArticle } from '@pages/PressReleaseArticle'
import { useGetLocale } from '@hooks/useGetLocale'
import { getPressReleaseArticle } from '@helpers/getPressReleaseArticle'
import { arrayIncludes } from '@helpers/arrayIncludes'
import { APP_LOCALES } from '@constants/i18n'
import { getStructuredDataForPressReleaseArticle } from '@helpers/getStructuredDataForPressReleaseArticle'
import { config } from '@infrastructure/config'
import { createPressReleaseBreadcrumb } from '@helpers/createPressReleaseBreadcrumb'
import { createStructuredDataForLogo } from '@helpers/createStructuredDataForLogo'
import { getPressReleases } from '@helpers/getPressReleases'
import { generatePageMeta } from '@helpers/generatePageMeta'
import { getCanonicalUrl } from '@helpers/getCanonicalUrl'
import { APP_FEATURES } from '@constants/features'
import { getCountryLanguageFromLocale } from '@helpers/getCountryLanguageFromLocale'

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const pressReleaseArticle = await getPressReleaseArticle(params, request)

  // We need to use some of the SEO data from the press releases page as individual
  // press releases don't have their own SEO modules
  const pressReleasesPageData = await getPressReleases(params, request)

  if (pressReleaseArticle && pressReleasesPageData) {
    return json(
      {
        ...pressReleasesPageData,
        ...pressReleaseArticle,
        websiteUrl: config.websiteUrl,
      },
      {
        headers: {
          'Cache-Control': 'no-store',
        },
      }
    )
  }

  throw new Response(null, {
    status: 404,
    statusText: 'Not Found',
  })
}

export const meta: MetaFunction<
  typeof loader,
  { 'routes/$locale': LocaleLoader }
> = ({ data, params, matches }) => {
  const { locale } = params
  if (!locale || !arrayIncludes(APP_LOCALES, locale)) return []

  const { country, language } = getCountryLanguageFromLocale(locale)

  const metaTags = []

  const { pressReleaseArticle, websiteUrl, pressReleasesData, pageVariations } =
    data as UnwrapLoaderData<typeof loader>

  const slug = `${APP_FEATURES.PressRelease.prefix[language]}/${params.slug}`
  const canonicalUrl = getCanonicalUrl({
    countryCodes: pressReleaseArticle.countryCodes,
    country,
    language,
    slug,
    baseUrl: websiteUrl,
  })
  const alternateUrls = [...Object.values(pageVariations)]

  metaTags.push({
    tagName: 'link',
    rel: 'canonical',
    href: canonicalUrl,
  })

  alternateUrls.flat().map((pageVariation: string) => {
    // Restrict alternate link tags to DE versions
    if (pageVariation.length > 6 && pageVariation.includes('/de-')) {
      const language = pageVariation.substring(1, 3)
      const country = pageVariation.substring(4, 6)

      metaTags.push({
        tagName: 'link',
        rel: 'alternate',
        href: `${websiteUrl}${pageVariation}`,
        hrefLang: `${language}-${country.toUpperCase()}`,
      })
    }
  })

  const linkedSeo = {
    pageTitle:
      pressReleaseArticle.title ||
      pressReleasesData.pressArchivePage.linkedSeo.pageTitle,
    seoDescription: pressReleaseArticle.excerpt,
    ogTitle: pressReleaseArticle.title,
    ogDescription: pressReleaseArticle.excerpt,
    ogUrl: canonicalUrl,
    ogImage: {
      url:
        pressReleaseArticle?.ogImage?.url ||
        pressReleasesData.pressArchivePage.linkedSeo.ogImage?.url,
    },
    shouldIndex: true,
  }

  const pageMeta = generatePageMeta(linkedSeo)

  metaTags.push(...pageMeta)

  const localeLoaderData = matches.find(
    (match: { id: string }) => match.id === 'routes/$locale'
  )

  let breadcrumbName

  if (localeLoaderData) {
    breadcrumbName = localeLoaderData?.data?.translations?.pressArchiveButton
  }

  const breadcrumbData = createPressReleaseBreadcrumb(
    websiteUrl,
    locale,
    breadcrumbName,
    data?.pressReleaseArticle.title
  )

  if (breadcrumbData) {
    metaTags.push({ 'script:ld+json': breadcrumbData })
  }

  const articleStructuredData = getStructuredDataForPressReleaseArticle(
    pressReleaseArticle,
    canonicalUrl,
    websiteUrl
  )

  if (articleStructuredData) {
    metaTags.push({ 'script:ld+json': articleStructuredData })
  }

  metaTags.push({ 'script:ld+json': createStructuredDataForLogo(websiteUrl) })

  const metaDescription = {
    name: 'description',
    content: linkedSeo?.ogDescription ?? linkedSeo?.seoDescription,
  }

  metaTags.push(metaDescription)

  // Note: when updated to Remix V2 the warning about non-unique keys will be gone
  // Check: https://github.com/remix-run/remix/issues/6953
  return metaTags
}

export default function Index() {
  const locale = useGetLocale()
  const loaderData = useLoaderData() as UnwrapLoaderData<typeof loader>

  return (
    locale &&
    arrayIncludes(APP_LOCALES, locale) && (
      <div className='py-32 px-16 sm:px-8 lg:px-12 flex justify-center'>
        <div className='max-w-press-release'>
          <PressReleaseArticle loaderData={loaderData} locale={locale} />
        </div>
      </div>
    )
  )
}
